import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useDesktopScope from './useDesktopScope';

export const statusObject = value => !!value && typeof value === 'object'
  && !Array.isArray(value) && typeof value.state === 'string';

// Read expiry retires adoption only: IPC work cannot be cancelled by a renderer timer.
export default function useDesktopObservation(method, event, options = {}) {
  const scope = useDesktopScope();
  const config = useRef(options); config.current = options;
  const enabled = options.enabled !== false;
  const owner = useMemo(() => ({ scope, method, event, enabled, sequence: 0, active: false, pending: new Set() }), [scope, method, event, enabled]);
  const [result, setResult] = useState(null);
  const available = enabled && typeof scope.bridge?.[method] === 'function';
  const owns = useCallback(() => owner.active && scope.current(), [owner, scope]);
  const publish = useCallback((value, sequence) => {
    if (!owns() || sequence !== owner.sequence) return null;
    const valid = (config.current.validate || statusObject)(value);
    setResult({ owner, phase: valid ? 'ready' : 'unavailable', status: valid ? value : null });
    return valid ? value : null;
  }, [owns, owner]);
  const refresh = useCallback(async () => {
    if (!owns() || !available) return null;
    const sequence = ++owner.sequence;
    setResult({ owner, phase: 'loading', status: null });
    let timer;
    let retire;
    const expiry = new Promise((_, reject) => {
      retire = () => reject(new Error('Desktop observation unavailable'));
      timer = setTimeout(retire, 30000);
    });
    owner.pending.add(retire);
    try {
      const value = await Promise.race([
        Promise.resolve().then(() => {
          if (!owns()) throw new Error('Desktop observation retired');
          return scope.bridge[method]();
        }), expiry,
      ]);
      return publish(value, sequence);
    } catch {
      if (owns() && sequence === owner.sequence) setResult({ owner, phase: 'unavailable', status: null });
      return null;
    } finally {
      clearTimeout(timer);
      owner.pending.delete(retire);
    }
  }, [owns, available, owner, scope, method, publish]);
  useEffect(() => {
    owner.active = true;
    const changed = value => {
      if (!owns()) return;
      if (config.current.invalidateEvent) { refresh(); return; }
      publish(value, ++owner.sequence);
    };
    let unsubscribe;
    if (enabled && typeof scope.bridge?.[event] === 'function') unsubscribe = scope.bridge[event](changed);
    const domEvent = config.current.domEvent;
    if (enabled && domEvent) window.addEventListener(domEvent, changed);
    refresh();
    return () => {
      owner.active = false;
      owner.sequence++;
      unsubscribe?.();
      if (domEvent) window.removeEventListener(domEvent, changed);
      for (const retire of owner.pending) retire();
    };
  }, [scope, owner, event, owns, publish, refresh, enabled]);
  const visible = result?.owner === owner && scope.current() ? result : null;
  const renderedSequence = owner.sequence;
  return {
    current: () => owns() && owner.sequence === renderedSequence,
    scope, available, status: visible?.status || null,
    phase: !available ? 'unsupported' : visible?.phase || 'loading',
    loading: available && (!visible || visible.phase === 'loading'),
    refresh,
    capture: () => value => publish(value, renderedSequence),
  };
}
